// src/pages/hreval/DailyWorkerEvaluation.jsx
import React, { useState, useEffect, useCallback } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { getAccessToken } from "../../auth";
import styles from "./DailyWorkerEvaluation.module.css";

const API_BASE = "/api/daily-workers";

const GRADES = ["전체", "S", "A", "B", "C", "F"];
const GRADE_ORDER = { S: 0, A: 1, B: 2, C: 3, F: 4 };

const GRADE_BADGE = {
  S: styles.badgeS, A: styles.badgeA, B: styles.badgeB,
  C: styles.badgeC, F: styles.badgeF,
};

const GRADE_COLOR = {
  S: { bg: "#fff8e1", border: "#f59e0b", text: "#b45309" },
  A: { bg: "#f0fdf4", border: "#22c55e", text: "#15803d" },
  B: { bg: "#eff6ff", border: "#3b82f6", text: "#1d4ed8" },
  C: { bg: "#fdf4ff", border: "#a855f7", text: "#7e22ce" },
  F: { bg: "#fff1f2", border: "#f43f5e", text: "#be123c" },
};

const EMPTY_FORM = {
  name: "", gender: "남", birth: "", phone: "",
  site: "", working: "Y", grade: "B",
};

const EMPTY_EVAL = { site: "", evaluator: "", content: "" };

/* ── API 헬퍼 ─────────────────────────────── */
async function apiFetch(url, options = {}) {
  const token = getAccessToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`${res.status}: ${err}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function groupBySite(list) {
  const order = [];
  const map = {};
  list.forEach((w) => {
    if (!map[w.site]) { map[w.site] = []; order.push(w.site); }
    map[w.site].push(w);
  });
  return order.map((site) => ({ site, workers: map[site] }));
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  const len = digits.length;
  if (len < 4)  return digits;
  if (len < 8)  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (len < 11) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return         `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export default function DailyWorkerEvaluation() {
  const modalBackdrop = useBackdropClose(() => closeModal());


  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQ, setSearchQ]         = useState("");
  const [gradeFilter, setGradeFilter] = useState("전체");

  const [editingKey, setEditingKey] = useState(null);
  const [evalDraft, setEvalDraft]   = useState(EMPTY_EVAL);

  const [modalOpen, setModalOpen]     = useState(false);
  const [modalEditId, setModalEditId] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);

  // ✅ 모바일 감지
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // ✅ 모바일 평가 토글 (워커 ID 집합)
  const [expandedEvals, setExpandedEvals] = useState(new Set());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ── 목록 조회 ────────────────────────────── */
  const loadWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQ) params.set("search", searchQ);
      if (gradeFilter !== "전체") params.set("grade", gradeFilter);
      const url = `${API_BASE}/?${params.toString()}`;
      const data = await apiFetch(url);
      let list = Array.isArray(data) ? data : (data.results || []);

      if (gradeFilter === "전체") {
        list = [...list].sort((a, b) => {
          const gradeDiff = (GRADE_ORDER[a.grade] ?? 99) - (GRADE_ORDER[b.grade] ?? 99);
          if (gradeDiff !== 0) return gradeDiff;
          return a.name.localeCompare(b.name, "ko");
        });
      }

      setWorkers(list);
    } catch (e) {
      console.error("[목록 조회 실패]", e);
      alert("직원 목록을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }, [searchQ, gradeFilter]);

  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const groups = gradeFilter === "전체"
    ? [{ site: null, workers }]
    : groupBySite(workers);

  /* ── 평가 CRUD ───────────────────────────── */
  const startAddEval = (workerId, defaultSite = "") => {
    setEditingKey(`${workerId}:new`);
    setEvalDraft({ site: defaultSite, evaluator: "", content: "" });
  };

  const startEditEval = (workerId, ev) => {
    setEditingKey(`${workerId}:${ev.id}`);
    setEvalDraft({
      site: ev.site || "",
      evaluator: ev.evaluator || "",
      content: ev.content || "",
    });
  };

  const cancelEditEval = () => {
    setEditingKey(null);
    setEvalDraft(EMPTY_EVAL);
  };

  const saveEval = async (workerId, evalId) => {
    try {
      if (evalId === "new") {
        await apiFetch(`${API_BASE}/${workerId}/evals/`, {
          method: "POST",
          body: JSON.stringify(evalDraft),
        });
      } else {
        await apiFetch(`${API_BASE}/${workerId}/evals/${evalId}/`, {
          method: "PATCH",
          body: JSON.stringify(evalDraft),
        });
      }
      cancelEditEval();
      await loadWorkers();
    } catch (e) {
      console.error("[평가 저장 실패]", e);
      alert("평가 저장에 실패했습니다.");
    }
  };

  const deleteEval = async (workerId, evalId) => {
    if (!window.confirm("이 평가를 삭제하시겠습니까?")) return;
    try {
      await apiFetch(`${API_BASE}/${workerId}/evals/${evalId}/`, { method: "DELETE" });
      await loadWorkers();
    } catch (e) {
      console.error("[평가 삭제 실패]", e);
      alert("평가 삭제에 실패했습니다.");
    }
  };

  /* ── 직원 삭제 ────────────────────────────── */
  const deleteWorker = async (id) => {
    if (!window.confirm("이 직원 정보를 삭제하시겠습니까?\n(평가 이력도 함께 삭제됩니다)")) return;
    try {
      await apiFetch(`${API_BASE}/${id}/`, { method: "DELETE" });
      cancelEditEval();
      await loadWorkers();
    } catch (e) {
      console.error("[직원 삭제 실패]", e);
      alert("삭제에 실패했습니다.");
    }
  };

  /* ── 모달 (직원 추가/편집) ───────────────── */
  const openModal = (editId = null) => {
    setModalEditId(editId);
    if (editId) {
      const w = workers.find((x) => x.id === editId);
      if (w) {
        setForm({
          name: w.name, gender: w.gender, birth: w.birth,
          phone: formatPhone(w.phone || ""), site: w.site,
          working: w.working, grade: w.grade,
        });
      }
    } else {
      setForm(EMPTY_FORM);
    }
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setForm((prev) => ({ ...prev, phone: formatPhone(value) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveModal = async () => {
    if (!form.name.trim()) { alert("이름을 입력해주세요."); return; }
    if (!form.birth)       { alert("생년월일을 입력해주세요."); return; }
    if (!form.site.trim()) { alert("근무지를 입력해주세요."); return; }

    try {
      const payload = {
        name: form.name, gender: form.gender, birth: form.birth,
        phone: form.phone, site: form.site, working: form.working,
        grade: form.grade,
      };
      if (modalEditId) {
        await apiFetch(`${API_BASE}/${modalEditId}/`, { method: "PATCH", body: JSON.stringify(payload) });
      } else {
        await apiFetch(`${API_BASE}/`, { method: "POST", body: JSON.stringify(payload) });
      }
      closeModal();
      await loadWorkers();
    } catch (e) {
      console.error("[저장 실패]", e);
      alert("저장에 실패했습니다.");
    }
  };

  /* ── 평가 토글 ───────────────────────────── */
  const toggleEval = (workerId) => {
    setExpandedEvals((prev) => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  let seq = 0;

  /* ══════════════════════════════════════════
     모바일 카드 렌더러
  ══════════════════════════════════════════ */
  const renderMobileCards = () => (
    <div style={{ padding: "0 0 80px 0" }}>
      {workers.length === 0 ? (
        <div style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "#999",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginTop: "8px",
          backgroundColor: "#fafafa",
        }}>
          {loading ? "불러오는 중..." : "등록된 직원이 없습니다."}
        </div>
      ) : (
        workers.map((w, idx) => {
          const evals = w.evals || [];
          const isExpanded = expandedEvals.has(w.id);
          const isAddingEval = editingKey === `${w.id}:new`;
          const gradeColor = GRADE_COLOR[w.grade] || { bg: "#f9fafb", border: "#d1d5db", text: "#374151" };

          return (
            <div
              key={w.id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
                marginBottom: "14px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              {/* ── 카드 헤더: 이름 + 등급 + 근무 상태 ── */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "13px 16px 12px",
                borderBottom: "2px solid #e2e8f0",
                backgroundColor: "#f8fafc",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                  }}>
                    {idx + 1}. {w.name}
                  </span>
                  <span style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    backgroundColor: gradeColor.bg,
                    border: `1px solid ${gradeColor.border}`,
                    color: gradeColor.text,
                    letterSpacing: "0.05em",
                  }}>
                    {w.grade}등급
                  </span>
                </div>
                <span style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  backgroundColor: w.working === "Y" ? "#dcfce7" : "#fee2e2",
                  color: w.working === "Y" ? "#166534" : "#991b1b",
                }}>
                  {w.working === "Y" ? "근무 중" : "휴무 중"}
                </span>
              </div>

              {/* ── 기본 정보 ── */}
              <div style={{ padding: "10px 16px 4px" }}>
                {[
                  { label: "성별",     value: w.gender },
                  { label: "생년월일", value: w.birth },
                  { label: "전화번호", value: w.phone || "-" },
                  { label: "근무지",   value: w.site },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}>
                    <span style={{
                      minWidth: "68px",
                      fontSize: "12px",
                      color: "#94a3b8",
                      fontWeight: "700",
                    }}>
                      {label}
                    </span>
                    <span style={{ fontSize: "13px", color: "#334155" }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* ── 관리 버튼 ── */}
              <div style={{
                display: "flex",
                gap: "8px",
                padding: "10px 16px",
                borderBottom: "1px solid #f1f5f9",
              }}>
                <button
                  type="button"
                  onClick={() => openModal(w.id)}
                  style={{
                    flex: 1,
                    padding: "7px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    color: "#475569",
                  }}
                >
                  ✏️ 편집
                </button>
                <button
                  type="button"
                  onClick={() => deleteWorker(w.id)}
                  style={{
                    flex: 1,
                    padding: "7px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    border: "1px solid #fca5a5",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    color: "#dc2626",
                  }}
                >
                  🗑️ 삭제
                </button>
              </div>

              {/* ── 평가 영역 토글 헤더 ── */}
              <div
                onClick={() => toggleEval(w.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 16px",
                  cursor: "pointer",
                  backgroundColor: "#f8fafc",
                  userSelect: "none",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: "700", color: "#475569" }}>
                  📋 평가 내용
                  {evals.length > 0 && (
                    <span style={{
                      marginLeft: "6px",
                      fontSize: "11px",
                      backgroundColor: "#dbeafe",
                      color: "#1d4ed8",
                      padding: "1px 6px",
                      borderRadius: "10px",
                      fontWeight: "700",
                    }}>
                      {evals.length}건
                    </span>
                  )}
                </span>
                <span style={{ fontSize: "14px", color: "#94a3b8" }}>
                  {isExpanded ? "▲" : "▼"}
                </span>
              </div>

              {/* ── 평가 목록 (펼쳤을 때) ── */}
              {isExpanded && (
                <div style={{ padding: "0 16px 14px" }}>

                  {/* 평가 추가 버튼 */}
                  {!isAddingEval && (
                    <button
                      type="button"
                      onClick={() => startAddEval(w.id, w.site)}
                      style={{
                        width: "100%",
                        padding: "8px 0",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                        border: "1px dashed #93c5fd",
                        borderRadius: "8px",
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        marginBottom: "10px",
                        marginTop: "4px",
                      }}
                    >
                      + 평가 추가
                    </button>
                  )}

                  {/* 새 평가 입력 폼 */}
                  {isAddingEval && (
                    <MobileEvalEditCard
                      draft={evalDraft}
                      setDraft={setEvalDraft}
                      onSave={() => saveEval(w.id, "new")}
                      onCancel={cancelEditEval}
                    />
                  )}

                  {/* 평가 없음 */}
                  {evals.length === 0 && !isAddingEval && (
                    <div style={{
                      padding: "14px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}>
                      평가 내용 없음
                    </div>
                  )}

                  {/* 평가 목록 */}
                  {evals.map((ev) => {
                    const isEditing = editingKey === `${w.id}:${ev.id}`;
                    return isEditing ? (
                      <MobileEvalEditCard
                        key={ev.id}
                        draft={evalDraft}
                        setDraft={setEvalDraft}
                        onSave={() => saveEval(w.id, ev.id)}
                        onCancel={cancelEditEval}
                      />
                    ) : (
                      <div key={ev.id} style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        marginBottom: "8px",
                        backgroundColor: "#fafafa",
                      }}>
                        {[
                          { label: "근무지",   value: ev.site || "-" },
                          { label: "평가자",   value: ev.evaluator || "-" },
                          { label: "평가 내용", value: ev.content || "-" },
                        ].map(({ label, value }) => (
                          <div key={label} style={{
                            display: "flex",
                            alignItems: "flex-start",
                            padding: "4px 0",
                            borderBottom: "1px solid #f1f5f9",
                          }}>
                            <span style={{
                              minWidth: "68px",
                              fontSize: "11px",
                              color: "#94a3b8",
                              fontWeight: "700",
                              paddingTop: "1px",
                            }}>
                              {label}
                            </span>
                            <span style={{
                              fontSize: "13px",
                              color: "#334155",
                              flex: 1,
                              lineHeight: "1.5",
                            }}>
                              {value}
                            </span>
                          </div>
                        ))}
                        <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
                          <button
                            type="button"
                            onClick={() => startEditEval(w.id, ev)}
                            style={{
                              flex: 1, padding: "6px 0", fontSize: "12px", fontWeight: "600",
                              cursor: "pointer", border: "1px solid #cbd5e1",
                              borderRadius: "6px", backgroundColor: "#fff", color: "#475569",
                            }}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteEval(w.id, ev.id)}
                            style={{
                              flex: 1, padding: "6px 0", fontSize: "12px", fontWeight: "600",
                              cursor: "pointer", border: "1px solid #fca5a5",
                              borderRadius: "6px", backgroundColor: "#fff", color: "#dc2626",
                            }}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  /* ══════════════════════════════════════════
     PC 테이블 렌더러 (기존 코드 그대로)
  ══════════════════════════════════════════ */
  const renderPCTable = () => (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <colgroup>
          <col style={{ width: "40px" }} />
          <col style={{ width: "68px" }} />
          <col style={{ width: "38px" }} />
          <col style={{ width: "86px" }} />
          <col style={{ width: "106px" }} />
          <col style={{ width: "64px" }} />
          <col style={{ width: "46px" }} />
          <col style={{ width: "84px" }} />
        </colgroup>
        <thead>
          <tr>
            <th>순서</th><th>이름</th><th>성별</th><th>생년월일</th>
            <th>전화번호</th><th>현재 근무</th><th>등급</th><th>관리</th>
          </tr>
        </thead>
        <tbody>
          {workers.length === 0 ? (
            <tr>
              <td colSpan={8} className={styles.empty}>
                {loading ? "불러오는 중..." : "등록된 직원이 없습니다."}
              </td>
            </tr>
          ) : (
            groups.map(({ site, workers: ws }) => (
              <React.Fragment key={site}>
                {site !== null && (
                  <tr className={styles.siteRow}>
                    <td colSpan={8}>
                      <span className={styles.sitePin}>📍</span>
                      {site}
                      <span className={styles.siteCnt}>{ws.length}명</span>
                    </td>
                  </tr>
                )}
                {ws.map((w) => {
                  seq++;
                  const currentSeq = seq;
                  const evals = w.evals || [];
                  const isAddingEval = editingKey === `${w.id}:new`;

                  return (
                    <React.Fragment key={w.id}>
                      <tr className={styles.infoRow}>
                        <td className={styles.tdNum}>{currentSeq}</td>
                        <td className={styles.tdName}>{w.name}</td>
                        <td>{w.gender}</td>
                        <td className={styles.tdSm}>{w.birth}</td>
                        <td className={styles.tdSm}>{w.phone}</td>
                        <td className={w.working === "Y" ? styles.workYes : styles.workNo}>
                          {w.working === "Y" ? "근무 중" : "휴무 중"}
                        </td>
                        <td>
                          <span className={[styles.badge, GRADE_BADGE[w.grade] || ""].join(" ")}>
                            {w.grade}
                          </span>
                        </td>
                        <td>
                          <div className={styles.manageBtns}>
                            <button type="button" className={styles.btnSm} onClick={() => openModal(w.id)}>편집</button>
                            <button type="button" className={[styles.btnSm, styles.btnDanger].join(" ")} onClick={() => deleteWorker(w.id)}>삭제</button>
                          </div>
                        </td>
                      </tr>
                      <tr className={styles.evalRow}>
                        <td colSpan={8}>
                          <div className={styles.evalArea}>
                            <div className={styles.evalHeader}>
                              <span className={styles.evalLabel}>▸ 평가 내용</span>
                              {!isAddingEval && (
                                <button
                                  type="button"
                                  className={[styles.btnSm, styles.btnBlue, styles.btnAddEval].join(" ")}
                                  onClick={() => startAddEval(w.id, w.site)}
                                >
                                  + 평가 추가
                                </button>
                              )}
                            </div>
                            <table className={styles.evalTable}>
                              <colgroup>
                                <col style={{ width: "100px" }} />
                                <col style={{ width: "90px" }} />
                                <col />
                                <col style={{ width: "100px" }} />
                              </colgroup>
                              <thead>
                                <tr>
                                  <th>근무지</th>
                                  <th>평가자</th>
                                  <th>평가 내용</th>
                                  <th>관리</th>
                                </tr>
                              </thead>
                              <tbody>
                                {isAddingEval && (
                                  <EvalEditRow
                                    draft={evalDraft}
                                    setDraft={setEvalDraft}
                                    onSave={() => saveEval(w.id, "new")}
                                    onCancel={cancelEditEval}
                                  />
                                )}
                                {evals.length === 0 && !isAddingEval && (
                                  <tr>
                                    <td colSpan={4} className={styles.evalEmpty}>
                                      평가 내용 없음
                                    </td>
                                  </tr>
                                )}
                                {evals.map((ev) => {
                                  const isEditing = editingKey === `${w.id}:${ev.id}`;
                                  return isEditing ? (
                                    <EvalEditRow
                                      key={ev.id}
                                      draft={evalDraft}
                                      setDraft={setEvalDraft}
                                      onSave={() => saveEval(w.id, ev.id)}
                                      onCancel={cancelEditEval}
                                    />
                                  ) : (
                                    <tr key={ev.id}>
                                      <td className={styles.evalCellSite}>{ev.site || "-"}</td>
                                      <td className={styles.evalCellEvaluator}>{ev.evaluator || "-"}</td>
                                      <td className={styles.evalCellContent}>{ev.content || "-"}</td>
                                      <td>
                                        <div className={styles.evalRowBtns}>
                                          <button type="button" className={styles.btnSm} onClick={() => startEditEval(w.id, ev)}>수정</button>
                                          <button type="button" className={[styles.btnSm, styles.btnDanger].join(" ")} onClick={() => deleteEval(w.id, ev.id)}>삭제</button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  /* ══════════════════════════════════════════
     메인 렌더
  ══════════════════════════════════════════ */
  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h2 className={styles.title}>일용직 인사평가</h2>
      </div>

      {/* 툴바 */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="이름, 근무지, 전화번호 검색..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>

        <div className={styles.chips}>
          {GRADES.map((g) => (
            <button
              key={g}
              type="button"
              className={[
                styles.chip,
                gradeFilter === g ? styles.chipActive : "",
                gradeFilter === g && g !== "전체" ? styles[`chip${g}`] : "",
              ].filter(Boolean).join(" ")}
              onClick={() => setGradeFilter(g)}
            >
              {g}
            </button>
          ))}
        </div>

        <span className={styles.count}>
          {loading ? "조회 중..." : `총 ${workers.length}명`}
        </span>

        <button type="button" className={styles.btnAdd} onClick={() => openModal()}>
          + 직원 추가
        </button>
      </div>

      {/* ✅ 모바일 / PC 분기 */}
      {isMobile ? renderMobileCards() : renderPCTable()}

      {/* 모달 (공통) */}
      {modalOpen && (
        <div className={styles.modalBg} {...modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>{modalEditId ? "직원 정보 편집" : "직원 추가"}</h3>

            <div className={styles.modalRow}>
              <div className={styles.field}><label>이름 *</label><input type="text" name="name" value={form.name} onChange={handleFormChange} placeholder="홍길동" /></div>
              <div className={styles.field}><label>성별</label><select name="gender" value={form.gender} onChange={handleFormChange}><option value="남">남</option><option value="여">여</option></select></div>
            </div>
            <div className={styles.modalRow}>
              <div className={styles.field}><label>생년월일 *</label><input type="date" name="birth" value={form.birth} onChange={handleFormChange} /></div>
              <div className={styles.field}><label>전화번호</label><input type="text" name="phone" value={form.phone} onChange={handleFormChange} placeholder="010-0000-0000" maxLength={13} /></div>
            </div>
            <div className={styles.modalRow}>
              <div className={styles.field}><label>근무지 *</label><input type="text" name="site" value={form.site} onChange={handleFormChange} placeholder="가산점" /></div>
              <div className={styles.field}><label>현재 근무</label><select name="working" value={form.working} onChange={handleFormChange}><option value="Y">근무 중</option><option value="N">휴무 중</option></select></div>
            </div>
            <div className={styles.modalRow}>
              <div className={styles.field}><label>등급</label><select name="grade" value={form.grade} onChange={handleFormChange}>{["S","A","B","C","F"].map((g) => <option key={g} value={g}>{g}</option>)}</select></div>
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.btnCancel} onClick={closeModal}>취소</button>
              <button type="button" className={styles.btnSavePrimary} onClick={saveModal}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── PC용 평가 편집 행 컴포넌트 ─────────────── */
function EvalEditRow({ draft, setDraft, onSave, onCancel }) {
  const update = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));
  return (
    <tr className={styles.evalEditTr}>
      <td>
        <input
          type="text"
          className={styles.evalInput}
          value={draft.site}
          onChange={(e) => update("site", e.target.value)}
          placeholder="가산점"
        />
      </td>
      <td>
        <input
          type="text"
          className={styles.evalInput}
          value={draft.evaluator}
          onChange={(e) => update("evaluator", e.target.value)}
          placeholder="홍길동"
        />
      </td>
      <td>
        <textarea
          className={styles.evalInputTextarea}
          value={draft.content}
          onChange={(e) => update("content", e.target.value)}
          placeholder="평가 내용을 입력하세요..."
          rows={2}
        />
      </td>
      <td>
        <div className={styles.evalRowBtns}>
          <button type="button" className={[styles.btnSm, styles.btnBlue].join(" ")} onClick={onSave}>저장</button>
          <button type="button" className={styles.btnSm} onClick={onCancel}>취소</button>
        </div>
      </td>
    </tr>
  );
}

/* ── 모바일용 평가 편집 카드 컴포넌트 ──────── */
function MobileEvalEditCard({ draft, setDraft, onSave, onCancel }) {
  const update = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));
  return (
    <div style={{
      border: "1px solid #93c5fd",
      borderRadius: "10px",
      padding: "12px 14px",
      marginBottom: "8px",
      backgroundColor: "#eff6ff",
    }}>
      {[
        { label: "근무지",   key: "site",      placeholder: "가산점",            type: "input" },
        { label: "평가자",   key: "evaluator", placeholder: "홍길동",            type: "input" },
        { label: "평가 내용", key: "content",  placeholder: "평가 내용을 입력하세요...", type: "textarea" },
      ].map(({ label, key, placeholder, type }) => (
        <div key={key} style={{ marginBottom: "8px" }}>
          <label style={{ fontSize: "11px", color: "#1d4ed8", fontWeight: "700", display: "block", marginBottom: "4px" }}>
            {label}
          </label>
          {type === "textarea" ? (
            <textarea
              value={draft[key]}
              onChange={(e) => update(key, e.target.value)}
              placeholder={placeholder}
              rows={2}
              style={{
                width: "100%", padding: "7px 10px", fontSize: "13px",
                border: "1px solid #93c5fd", borderRadius: "6px",
                boxSizing: "border-box", resize: "vertical",
                backgroundColor: "#fff",
              }}
            />
          ) : (
            <input
              type="text"
              value={draft[key]}
              onChange={(e) => update(key, e.target.value)}
              placeholder={placeholder}
              style={{
                width: "100%", padding: "7px 10px", fontSize: "13px",
                border: "1px solid #93c5fd", borderRadius: "6px",
                boxSizing: "border-box", backgroundColor: "#fff",
              }}
            />
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
        <button
          type="button"
          onClick={onSave}
          style={{
            flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: "700",
            cursor: "pointer", border: "none", borderRadius: "8px",
            backgroundColor: "#2563eb", color: "#fff",
          }}
        >
          저장
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: "600",
            cursor: "pointer", border: "1px solid #cbd5e1",
            borderRadius: "8px", backgroundColor: "#fff", color: "#64748b",
          }}
        >
          취소
        </button>
      </div>
    </div>
  );
}